import { useState, useEffect } from 'react';
import api from '@/lib/api';
import { Loader2 } from 'lucide-react';
import InteractiveMessage from './InteractiveMessage';

/**
 * ChatbotPreview — vista previa EN VIVO de un nodo del chat_tree: "así lo verá el lead".
 *
 * NO reimplementa el motor: le pide al backend (POST .../chatbot-tree/{company_id}/preview
 * con { node }) que devuelva los `messages` (bodies v3 que produce chatbot_visual de SAAS),
 * y solo los DIBUJA con InteractiveMessage. Fuente única de verdad del render = el backend.
 *
 * Reusable: preview del configurador (b). El display en el chat (a) reusa InteractiveMessage aparte.
 * Contrato de flows (F3b): POST /api/admin/anchor/chatbot-tree/{company_id}/preview {node} -> {messages:[...]}.
 */
export default function ChatbotPreview({ companyId, node }) {
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!companyId || !node) { setMessages([]); setLoading(false); return; }
    let cancel = false;
    setLoading(true); setError('');  // "cargando" de inmediato: evita parpadear el vacío durante el debounce
    // Debounce: no martillar el endpoint en cada tecla mientras editan el nodo.
    const t = setTimeout(() => {
      api.post(`/admin/anchor/chatbot-tree/${companyId}/preview`, { node })
        .then((r) => { if (!cancel) setMessages(Array.isArray(r.data?.messages) ? r.data.messages : []); })
        .catch((e) => { if (!cancel) setError(e.response?.data?.detail || 'No se pudo generar la vista previa'); })
        .finally(() => { if (!cancel) setLoading(false); });
    }, 400);
    return () => { cancel = true; clearTimeout(t); };
  }, [companyId, JSON.stringify(node)]);

  return (
    <div className="rounded-xl bg-black/30 border border-white/10 p-3 space-y-2 min-h-[120px]" data-testid="chatbot-preview">
      <p className="text-[10px] uppercase tracking-wide text-white/40">Vista previa — así lo verá el lead</p>
      {loading && (
        <div className="flex items-center gap-2 text-xs text-white/50">
          <Loader2 className="w-3.5 h-3.5 animate-spin" /> Generando…
        </div>
      )}
      {error && <p className="text-xs text-amber-400">{error}</p>}
      {!loading && !error && messages.length === 0 && (
        <p className="text-xs text-white/40 italic">
          {node
            ? 'Este paso no envía un mensaje al lead (es una acción interna).'
            : 'Edita el nodo para ver la vista previa.'}
        </p>
      )}
      <div className="space-y-2">
        {messages.map((m, i) => <InteractiveMessage key={i} message={m} />)}
      </div>
    </div>
  );
}
